import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import {
  FiClock, FiCheck, FiX, FiArrowRight, FiArrowUpRight, FiShield,
  FiAlertTriangle, FiCalendar, FiFile, FiMapPin, FiSmartphone, FiUsers,
  FiBriefcase, FiZap, FiPlus, FiPause, FiBarChart2, FiCloud,
  FiLock, FiPieChart, FiSettings, FiSearch, FiBell,
  FiPhone, FiMail, FiHelpCircle, FiTablet, FiHeadphones,
} from 'react-icons/fi'
import { Logo, Eyebrow, Pill, Reveal, Counter, SectionHeader } from '../ui'
import { blogPosts } from '../data/blogPosts'

/* ── Helpers ─────────────────────────────────────────────── */

const trackCta = (ubicacion, plan = 'avanzado') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'cta_registro_click', { plan, ubicacion })
  }
}

const AvatarInitial = ({ initials, hue = 220, size = 28 }) => (
  <span
    className="inline-flex items-center justify-center text-white font-semibold"
    style={{
      width: size, height: size, borderRadius: '50%',
      fontSize: size * 0.38,
      background: `linear-gradient(135deg, oklch(0.62 0.16 ${hue}), oklch(0.55 0.20 ${hue + 50}))`,
    }}
    aria-hidden="true"
  >
    {initials}
  </span>
)

const TrafficDots = () => (
  <span className="flex gap-1.5">
    <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
    <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
    <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
  </span>
)

/* ── Mockups ─────────────────────────────────────────────── */

function AdminDashboardMockup({ compact = false }) {
  return (
    <div className="ui-card overflow-hidden" style={{ minWidth: 0 }}>
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--line)] bg-[var(--bg-2)]/60">
        <TrafficDots />
        <div className="flex-1 mx-2">
          <div className="h-6 rounded-md bg-[rgba(11,11,15,.06)] flex items-center px-3 text-[11px] font-mono text-[var(--ink-3)]">
            <FiLock size={11} className="mr-1.5" /> app.presenzia.app/dashboard
          </div>
        </div>
        <div className="flex items-center gap-2 text-[var(--ink-3)]">
          <FiSearch size={14} />
          <FiBell size={14} />
        </div>
      </div>

      <div className="grid grid-cols-12">
        <aside className="col-span-3 border-r border-[var(--line)] bg-[var(--bg-2)]/40 p-3 hidden md:block">
          <div className="flex items-center gap-2 mb-4 px-2">
            <span className="w-6 h-6 rounded-md ia-grad-bg grid place-items-center">
              <FiClock size={13} className="text-white" strokeWidth={2} />
            </span>
            <span className="font-semibold text-sm">PresenzIA</span>
          </div>
          <div className="text-[10px] font-mono text-[var(--ink-3)] uppercase tracking-wider mb-2 px-2">Empresa</div>
          <div className="px-2 py-1.5 rounded-md bg-[rgba(11,11,15,.04)] text-xs font-medium mb-3 flex items-center justify-between">
            <span>Climatización Med…</span>
            <FiArrowRight size={11} className="opacity-50" />
          </div>
          <nav className="space-y-0.5 text-[12.5px]">
            {[
              ['Resumen', FiPieChart, true],
              ['Fichajes', FiClock],
              ['Empleados', FiUsers],
              ['Ausencias', FiCalendar],
              ['Informes', FiBarChart2],
              ['Geolocalización', FiMapPin],
              ['Ajustes', FiSettings],
            ].map(([label, I, active], i) => (
              <div key={i} className={`flex items-center gap-2 px-2 py-1.5 rounded-md ${active ? 'bg-[rgba(37,99,235,.08)] text-[#2563EB] font-medium' : 'text-[var(--ink-2)]'}`}>
                <I size={13} /> {label}
              </div>
            ))}
          </nav>
          <div className="mt-5 p-3 rounded-lg border border-[var(--line)] bg-white/60">
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)] mb-1">Plan</div>
            <div className="text-xs font-semibold">Avanzado · 12 emp.</div>
            <div className="mt-2 h-1 rounded-full bg-[rgba(11,11,15,.08)] overflow-hidden">
              <div className="h-full w-3/5 ia-grad-bg" />
            </div>
          </div>
        </aside>

        <main className="col-span-12 md:col-span-9 p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-mono text-[var(--ink-3)] uppercase tracking-wider">Lunes 13 abril 2026</div>
              <div className="text-[18px] font-semibold mt-0.5">Buenos días, Rosa</div>
            </div>
            <Pill tone="emerald"><FiCheck size={11} /> Cumplimiento legal · al día</Pill>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            {[
              { l: 'Fichajes hoy', v: '11/12', s: '1 ausencia justificada', tone: 'text-[#2563EB]' },
              { l: 'Horas registradas', v: '84,5h', s: '+2,3h vs. semana ant.', tone: 'text-[#10B981]' },
              { l: 'Archivo legal', v: '4 años', s: 'Conservación garantizada', tone: 'text-[#7C3AED]' },
            ].map((k, i) => (
              <div key={i} className="p-3 rounded-xl border border-[var(--line)] bg-[var(--bg)]">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)]">{k.l}</div>
                <div className={`text-2xl font-semibold mt-1 ${k.tone}`}>{k.v}</div>
                <div className="text-[11px] text-[var(--ink-3)] mt-0.5">{k.s}</div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-[var(--line)] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--bg-2)]/40 border-b border-[var(--line)]">
              <div className="text-xs font-semibold">Fichajes de hoy</div>
              <div className="flex items-center gap-2 text-[11px] text-[var(--ink-3)]">
                <span className="ui-chip">Tiempo real</span>
                <FiArrowRight size={11} />
              </div>
            </div>
            <div className="divide-y divide-[var(--line)]">
              {[
                ['MG', 'María García', 195, 'Diseñadora', '08:58', '—', 'ok'],
                ['CL', 'Carlos López', 250, 'Comercial', '09:02', '13:30', 'pause'],
                ['AM', 'Ana Martínez', 25, 'Soporte', '09:05', '—', 'ok'],
                ['JR', 'Javier Ruiz', 130, 'Técnico', '—', '—', 'absent'],
              ].map(([init, name, hue, role, ein, eout, st], i) => (
                <div key={i} className="grid grid-cols-12 items-center px-4 py-2.5 text-[12.5px]">
                  <div className="col-span-5 flex items-center gap-2.5">
                    <AvatarInitial initials={init} hue={hue} size={26} />
                    <div>
                      <div className="font-medium leading-tight">{name}</div>
                      <div className="text-[10.5px] text-[var(--ink-3)]">{role}</div>
                    </div>
                  </div>
                  <div className="col-span-2 font-mono tnum text-[12px] text-[var(--ink-2)]">{ein}</div>
                  <div className="col-span-2 font-mono tnum text-[12px] text-[var(--ink-3)]">{eout}</div>
                  <div className="col-span-3 text-right">
                    {st === 'ok' && <Pill tone="emerald"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> En jornada</Pill>}
                    {st === 'pause' && <Pill tone="amber"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" /> En pausa</Pill>}
                    {st === 'absent' && <Pill tone="red"><span className="w-1.5 h-1.5 rounded-full bg-[#EF4444]" /> Ausencia just.</Pill>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {!compact && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="rounded-xl border border-[var(--line)] p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)] mb-1.5">Horas semana</div>
                <div className="flex items-end gap-1.5 h-14">
                  {[40, 70, 55, 80, 65, 30, 10].map((h, i) => (
                    <div key={i} className="flex-1 rounded-t-md ia-grad-bg" style={{ height: `${h}%`, opacity: i === 6 ? 0.35 : 1 }} />
                  ))}
                </div>
                <div className="grid grid-cols-7 mt-1.5 text-[9px] font-mono text-[var(--ink-3)] uppercase">
                  {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(d => <div key={d} className="text-center">{d}</div>)}
                </div>
              </div>
              <div className="rounded-xl border border-[var(--line)] p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)] mb-1.5">Próximas ausencias</div>
                <div className="space-y-1.5 text-[11.5px]">
                  <div className="flex items-center justify-between"><span>Carlos L. · Vacaciones</span><span className="font-mono text-[var(--ink-3)]">15–22 abr</span></div>
                  <div className="flex items-center justify-between"><span>Ana M. · Médico</span><span className="font-mono text-[var(--ink-3)]">17 abr</span></div>
                  <div className="flex items-center justify-between"><span>María G. · Asuntos prop.</span><span className="font-mono text-[var(--ink-3)]">23 abr</span></div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

function ClockMockup() {
  const [t, setT] = useState(new Date())
  useEffect(() => {
    const id = setInterval(() => setT(new Date()), 1000)
    return () => clearInterval(id)
  }, [])
  const hh = String(t.getHours()).padStart(2, '0')
  const mm = String(t.getMinutes()).padStart(2, '0')
  const ss = String(t.getSeconds()).padStart(2, '0')
  return (
    <div className="ui-card p-6 max-w-sm mx-auto">
      <div className="flex items-center justify-between text-[var(--ink-3)] text-[11px] font-mono uppercase tracking-wider mb-4">
        <span>Lunes · 13 abr</span>
        <Pill tone="emerald"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> En jornada</Pill>
      </div>
      <div className="text-center py-3">
        <div className="font-display text-7xl tracking-tight tnum">{hh}<span className="text-[var(--ink-3)]">:</span>{mm}<span className="text-[var(--ink-3)]">:</span>{ss}</div>
        <div className="text-[12px] text-[var(--ink-3)] mt-2">María García · entrada 08:58</div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4">
        <button className="py-2.5 rounded-xl text-[11.5px] font-medium border border-[var(--line)] bg-[var(--bg-2)]/60">Entrada</button>
        <button className="py-2.5 rounded-xl text-[11.5px] font-medium border border-[var(--line)] bg-[var(--bg-2)]/60 flex items-center justify-center gap-1"><FiPause size={11} /> Pausa</button>
        <button className="py-2.5 rounded-xl text-[11.5px] font-semibold text-white" style={{ background: '#0B0B0F' }}>Registrar salida</button>
      </div>
      <div className="mt-4 pt-4 border-t border-[var(--line)] text-[11px] text-[var(--ink-3)] font-mono flex items-center gap-2">
        <FiMapPin size={11} /> C/ Mayor 14 · 41,38° N
        <span className="ml-auto"><FiCheck size={11} className="inline mr-1 text-[#10B981]" />Geofence OK</span>
      </div>
    </div>
  )
}

function CalendarMockup() {
  const days = []
  for (let i = 0; i < 2; i++) days.push({ pad: true })
  for (let d = 1; d <= 30; d++) {
    let kind = 'wd'
    const dow = (d + 1) % 7
    if (dow === 4 || dow === 5) kind = 'we'
    if ([6, 7, 8, 9, 10, 11].includes(d)) kind = 'vac'
    if ([1, 14].includes(d)) kind = 'fest'
    days.push({ d, kind })
  }
  return (
    <div className="ui-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)]">Ausencias · equipo</div>
          <div className="font-semibold text-[15px] mt-0.5">Abril 2026</div>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-[#10B981]" /> Vacaciones</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-[#EF4444]" /> Festivo</span>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5 text-center">
        {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map(d => (
          <div key={d} className="text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)] py-1">{d}</div>
        ))}
        {days.map((c, i) => {
          if (c.pad) return <div key={i} />
          const cls =
            c.kind === 'vac' ? 'bg-[#10B981]/15 text-[#0F8C66] border-[#10B981]/30' :
            c.kind === 'fest' ? 'bg-[#EF4444]/12 text-[#C13030] border-[#EF4444]/25' :
            c.kind === 'we' ? 'bg-transparent text-[var(--ink-3)] border-transparent' :
            'border-[var(--line)] text-[var(--ink-2)]'
          return (
            <div key={i} className={`aspect-square rounded-lg border text-[12px] font-medium grid place-items-center ${cls}`}>
              {c.d}
            </div>
          )
        })}
      </div>
      <div className="mt-4 pt-4 border-t border-[var(--line)] flex items-center gap-3 text-[11.5px]">
        <AvatarInitial initials="CL" hue={250} size={22} />
        <span><span className="font-medium">Carlos L.</span> · vacaciones aprobadas <span className="font-mono text-[var(--ink-3)]">6–11 abr</span></span>
        <span className="ml-auto"><Pill tone="emerald"><FiCheck size={10} /> Aprobado</Pill></span>
      </div>
    </div>
  )
}

function ReportMockup() {
  const rows = [
    ['MG', 'María García', 220, '168h', '4h', 'ok'],
    ['CL', 'Carlos López', 250, '152h', '0h', 'ok'],
    ['AM', 'Ana Martínez', 25, '176h', '12h', 'warn'],
    ['JR', 'Javier Ruiz', 130, '144h', '2h', 'ok'],
  ]
  return (
    <div className="ui-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)]">Informe mensual · marzo 2026</div>
          <div className="font-semibold text-[15px] mt-0.5">Cumplimiento RD-Ley 8/2019</div>
        </div>
        <div className="flex gap-1.5">
          <Pill tone="red"><FiFile size={10} /> PDF</Pill>
          <Pill tone="emerald"><FiFile size={10} /> Excel</Pill>
        </div>
      </div>
      <div className="rounded-xl border border-[var(--line)] overflow-hidden">
        <div className="grid grid-cols-12 px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)] bg-[var(--bg-2)]/50 border-b border-[var(--line)]">
          <div className="col-span-5">Empleado</div>
          <div className="col-span-3">Horas</div>
          <div className="col-span-2">Extras</div>
          <div className="col-span-2 text-right">Estado</div>
        </div>
        {rows.map(([init, name, hue, h, e, st], i) => (
          <div key={i} className="grid grid-cols-12 px-3 py-2.5 items-center text-[12.5px] border-t border-[var(--line)] first:border-t-0">
            <div className="col-span-5 flex items-center gap-2.5">
              <AvatarInitial initials={init} hue={hue} size={22} />
              <span className="font-medium">{name}</span>
            </div>
            <div className="col-span-3 font-mono tnum">{h}</div>
            <div className="col-span-2 font-mono tnum text-[var(--ink-3)]">{e}</div>
            <div className="col-span-2 text-right">
              {st === 'ok'
                ? <Pill tone="emerald"><FiCheck size={10} /> OK</Pill>
                : <Pill tone="amber"><FiAlertTriangle size={10} /> Revisar</Pill>}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px] text-[var(--ink-3)]">
        <span><FiShield size={11} className="inline mr-1" /> Archivo conservado · 4 años</span>
        <span className="font-mono">EXP-2026-03-CMA</span>
      </div>
    </div>
  )
}

function MapMockup() {
  return (
    <div className="ui-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)]">Geolocalización · hoy</div>
          <div className="font-semibold text-[15px] mt-0.5">Fichajes en zona · 8 de 9</div>
        </div>
        <Pill tone="brand"><FiMapPin size={10} /> Multi-centro</Pill>
      </div>
      <div className="relative aspect-[4/3] rounded-xl overflow-hidden border border-[var(--line)] bg-[var(--bg-2)]">
        <svg viewBox="0 0 400 300" className="absolute inset-0 w-full h-full">
          <defs>
            <pattern id="lv2-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M40 0H0V40" fill="none" stroke="currentColor" strokeOpacity=".08" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="400" height="300" fill="url(#lv2-grid)" />
          <path d="M0 110 Q100 90 200 130 T400 100" stroke="currentColor" strokeOpacity=".18" strokeWidth="14" fill="none" strokeLinecap="round" />
          <path d="M50 0 Q70 150 30 300" stroke="currentColor" strokeOpacity=".14" strokeWidth="10" fill="none" strokeLinecap="round" />
          <path d="M260 0 Q280 150 320 300" stroke="currentColor" strokeOpacity=".12" strokeWidth="8" fill="none" strokeLinecap="round" />
          <circle cx="180" cy="150" r="80" fill="#2563EB" fillOpacity=".06" stroke="#2563EB" strokeOpacity=".4" strokeDasharray="4 6" />
        </svg>
        {[
          { x: '44%', y: '48%', c: '#10B981' },
          { x: '52%', y: '55%', c: '#10B981' },
          { x: '40%', y: '58%', c: '#10B981' },
          { x: '48%', y: '42%', c: '#10B981' },
          { x: '56%', y: '60%', c: '#10B981' },
          { x: '78%', y: '30%', c: '#F97316' },
          { x: '20%', y: '72%', c: '#10B981' },
          { x: '62%', y: '44%', c: '#10B981' },
          { x: '32%', y: '44%', c: '#10B981' },
        ].map((p, i) => (
          <span key={i} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: p.x, top: p.y }}>
            <span className="block w-3.5 h-3.5 rounded-full ring-2 ring-white" style={{ background: p.c, boxShadow: `0 0 0 6px ${p.c}22` }} />
          </span>
        ))}
        <div className="absolute bottom-3 left-3 ui-chip bg-white/90 text-[11px]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] mr-1" /> En zona &nbsp;
          <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] mx-1" /> Fuera
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
        <div className="rounded-lg border border-[var(--line)] p-2">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">Centro Norte</div>
          <div className="font-semibold mt-0.5">5 fichajes</div>
        </div>
        <div className="rounded-lg border border-[var(--line)] p-2">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">Centro Sur</div>
          <div className="font-semibold mt-0.5">3 fichajes</div>
        </div>
        <div className="rounded-lg border border-[var(--line)] p-2">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--ink-3)]">Fuera de zona</div>
          <div className="font-semibold mt-0.5 text-[#F97316]">1 fichaje</div>
        </div>
      </div>
    </div>
  )
}

function MobileMockup() {
  return (
    <div className="relative mx-auto" style={{ width: 260, height: 530 }}>
      <div
        className="absolute inset-0 rounded-[44px] border border-[var(--line-2)] bg-[var(--bg)] shadow-[0_30px_80px_-30px_rgba(11,11,15,.4)]"
        style={{ padding: 8 }}
      >
        <div className="relative w-full h-full rounded-[36px] overflow-hidden bg-[var(--bg)] border border-[var(--line)]">
          <div className="absolute left-1/2 -translate-x-1/2 top-1.5 w-20 h-5 rounded-full bg-[var(--ink)]" />
          <div className="px-5 pt-2.5 pb-1 flex justify-between text-[10px] font-mono text-[var(--ink-2)] tnum">
            <span>9:02</span><span>•••• 5G</span>
          </div>
          <div className="px-4 pt-3">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-lg ia-grad-bg grid place-items-center"><FiClock size={14} className="text-white" strokeWidth={2} /></span>
              <div className="text-[11px] font-semibold leading-tight">PresenzIA<br /><span className="text-[10px] text-[var(--ink-3)] font-normal">María García</span></div>
              <span className="ml-auto"><Pill tone="emerald"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> En jornada</Pill></span>
            </div>
            <div className="mt-5 text-center">
              <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)]">Hoy · entrada 08:58</div>
              <div className="font-display text-[56px] leading-none mt-2 tnum">09:02<span className="text-[var(--ink-3)] text-[28px] align-top">:34</span></div>
              <div className="text-[11px] text-[var(--ink-3)] mt-1.5">02h 04min trabajadas</div>
            </div>
            <button className="mt-5 w-full py-3.5 rounded-2xl text-white font-semibold text-[13px] ia-grad-bg shadow-[0_12px_24px_-8px_rgba(124,58,237,.45)]">
              Registrar pausa
            </button>
            <button className="mt-2 w-full py-3 rounded-2xl border border-[var(--line)] text-[12px] font-medium">
              Registrar salida
            </button>
            <div className="mt-4 rounded-xl border border-[var(--line)] p-3 text-[11px]">
              <div className="font-mono uppercase tracking-wider text-[10px] text-[var(--ink-3)] mb-1">Esta semana</div>
              <div className="flex items-end gap-1 h-10">
                {[60, 80, 55, 90, 40].map((h, i) => (
                  <div key={i} className="flex-1 rounded-sm ia-grad-bg" style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>
          </div>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-24 h-1 rounded-full bg-[var(--ink)] opacity-50" />
        </div>
      </div>
    </div>
  )
}

function MultiCompanyMockup() {
  const companies = [
    ['CMA', 'Climatización Med.', 220, 12, 'ok'],
    ['FIT', 'Ferretería I. Torres', 30, 8, 'ok'],
    ['MOR', 'Asesoría Morales', 270, 5, 'ok'],
    ['ELC', 'Electricidad Cano', 180, 14, 'warn'],
    ['CAL', 'Calzados Vega', 340, 22, 'ok'],
    ['SCI', 'Seguridad Incen. SL', 0, 17, 'ok'],
  ]
  return (
    <div className="ui-card overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--line)] bg-[var(--bg-2)]/60">
        <TrafficDots />
        <div className="flex-1 mx-2">
          <div className="h-6 rounded-md bg-[rgba(11,11,15,.06)] flex items-center px-3 text-[11px] font-mono text-[var(--ink-3)]">
            <FiBriefcase size={11} className="mr-1.5" /> asesorias.presenzia.app
          </div>
        </div>
        <Pill tone="purple"><FiBriefcase size={10} /> Asesoría</Pill>
      </div>
      <div className="p-5">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)]">Panel multi-empresa</div>
            <div className="font-semibold text-[16px] mt-0.5">6 clientes · 78 empleados</div>
          </div>
          <div className="flex gap-2">
            <button className="ui-chip"><FiPlus size={11} /> Añadir cliente</button>
            <button className="ui-chip"><FiFile size={11} /> Exportar todo</button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {companies.map(([init, name, hue, n, st], i) => (
            <div key={i} className="rounded-xl border border-[var(--line)] p-3 flex items-center gap-3">
              <span
                className="w-9 h-9 rounded-lg grid place-items-center text-white font-semibold text-[11px]"
                style={{ background: `linear-gradient(135deg, oklch(0.62 0.16 ${hue}), oklch(0.55 0.20 ${hue + 50}))` }}
              >{init}</span>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-medium truncate">{name}</div>
                <div className="text-[10.5px] text-[var(--ink-3)] tnum">{n} empleados · al día</div>
              </div>
              {st === 'ok'
                ? <Pill tone="emerald"><FiCheck size={10} /></Pill>
                : <Pill tone="amber"><FiAlertTriangle size={10} /></Pill>}
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-[var(--line)] flex items-center justify-between text-[11.5px]">
          <span className="text-[var(--ink-3)]"><FiZap size={11} className="inline mr-1 text-[#7C3AED]" /> Comisión recurrente por cliente activo</span>
          <span className="font-mono text-[var(--ink-2)] font-medium">+247 € / mes</span>
        </div>
      </div>
    </div>
  )
}

function KioscoMockup() {
  const team = [
    { i: 'MG', n: 'María G.', hue: 220 },
    { i: 'JR', n: 'Javier R.', hue: 12 },
    { i: 'LP', n: 'Lucía P.', hue: 280 },
    { i: 'AS', n: 'Andrés S.', hue: 160 },
    { i: 'CV', n: 'Carmen V.', hue: 340 },
    { i: 'TM', n: 'Tomás M.', hue: 40 },
    { i: 'RN', n: 'Raquel N.', hue: 200 },
    { i: 'DF', n: 'Diego F.', hue: 100 },
  ]
  return (
    <div className="relative mx-auto max-w-md">
      {/* Marco tablet */}
      <div className="rounded-[28px] border-[10px] border-[#0B0B0F] bg-[#0B0B0F] shadow-2xl">
        <div className="rounded-[18px] bg-white overflow-hidden">
          {/* Cabecera centro */}
          <div className="px-5 py-4 border-b border-[var(--line)] flex items-center justify-between bg-[var(--bg-2)]/60">
            <div className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-md bg-gradient-to-br from-indigo-500 to-cyan-500 grid place-items-center text-white text-[10px] font-bold">P</span>
              <div className="leading-tight">
                <div className="text-[12px] font-semibold">Centro · Almacén Norte</div>
                <div className="text-[10px] text-[var(--ink-3)] font-mono">Mar 14:32 · 23 empleados</div>
              </div>
            </div>
            <Pill tone="emerald"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" /> Quiosco activo</Pill>
          </div>
          {/* Grid empleados */}
          <div className="p-4 grid grid-cols-4 gap-2.5">
            {team.map((p, i) => (
              <div key={i} className="aspect-square rounded-xl border border-[var(--line)] bg-white grid place-items-center text-center hover:border-[var(--line-2)]">
                <div>
                  <div
                    className="w-9 h-9 rounded-full mx-auto grid place-items-center text-white font-semibold text-[11px]"
                    style={{ background: `linear-gradient(135deg, oklch(0.62 0.16 ${p.hue}), oklch(0.55 0.20 ${p.hue + 50}))` }}
                  >{p.i}</div>
                  <div className="text-[10px] mt-1.5 text-[var(--ink-2)] truncate px-1">{p.n}</div>
                </div>
              </div>
            ))}
          </div>
          {/* Footer instrucción */}
          <div className="px-5 py-3 border-t border-[var(--line)] bg-[var(--bg-2)]/60 flex items-center gap-2 text-[11.5px]">
            <FiUsers size={12} className="text-[var(--ink-3)]" />
            <span className="text-[var(--ink-3)]">Toca tu inicial e introduce tu PIN</span>
            <span className="ml-auto font-mono text-[10.5px] text-[var(--ink-3)]"><FiLock size={10} className="inline mr-1" /> PIN 5 dígitos</span>
          </div>
        </div>
      </div>
      {/* Etiqueta flotante */}
      <div className="hidden md:flex absolute -right-3 top-12 ui-card px-3 py-2 items-center gap-2 text-[11px]">
        <FiCheck size={11} className="text-[#10B981]" />
        <span className="font-medium">Fichaje en 3 segundos</span>
      </div>
    </div>
  )
}

function FichajeAsistidoMockup() {
  const semana = [
    { dia: 'Lun · 28 abr', e: '08:00', s: '17:00', estado: 'ok' },
    { dia: 'Mar · 29 abr', e: '08:05', s: '17:00', estado: 'ok' },
    { dia: 'Mié · 30 abr', e: '08:00', s: '17:30', estado: 'ok' },
    { dia: 'Jue · 01 may', e: '—',     s: '—',     estado: 'fest' },
    { dia: 'Vie · 02 may', e: '08:00', s: '15:00', estado: 'pend' },
  ]
  return (
    <div className="relative grid grid-cols-1 md:grid-cols-12 gap-4 max-w-2xl mx-auto">
      {/* Panel responsable: planificación de la semana */}
      <div className="md:col-span-7 ui-card overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--line)] bg-[var(--bg-2)]/60 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-md bg-[rgba(124,58,237,.12)] grid place-items-center text-[#7C3AED]"><FiBriefcase size={13} /></span>
            <div className="leading-tight">
              <div className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--ink-3)]">Vista responsable</div>
              <div className="text-[12px] font-semibold">Equipo Almacén · Sem. 18</div>
            </div>
          </div>
          <Pill tone="purple">Pre-rellenado</Pill>
        </div>
        <div className="p-4 space-y-1.5">
          {semana.map((d, i) => (
            <div key={i} className="grid grid-cols-12 items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-[var(--bg-2)]/40">
              <div className="col-span-5 text-[12px] text-[var(--ink-2)]">{d.dia}</div>
              <div className="col-span-3 font-mono text-[12px] tnum text-[var(--ink-2)]">{d.e}</div>
              <div className="col-span-3 font-mono text-[12px] tnum text-[var(--ink-2)]">{d.s}</div>
              <div className="col-span-1 text-right">
                {d.estado === 'ok' && <FiCheck size={12} className="inline text-[#10B981]" />}
                {d.estado === 'pend' && <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] inline-block" />}
                {d.estado === 'fest' && <span className="text-[10px] font-mono text-[var(--ink-3)]">FES</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 py-2.5 border-t border-[var(--line)] flex items-center gap-2 text-[11px] bg-[var(--bg-2)]/40">
          <FiUsers size={11} className="text-[var(--ink-3)]" />
          <span className="text-[var(--ink-3)]">5 empleados · 4 días</span>
          <span className="ml-auto font-mono text-[10.5px] text-[var(--ink-3)]">Aplicado al grupo</span>
        </div>
      </div>

      {/* Panel empleado: validación */}
      <div className="md:col-span-5 ui-card overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--line)] bg-[var(--bg-2)]/60 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className="w-7 h-7 rounded-full grid place-items-center text-white font-semibold text-[10px]"
              style={{ background: 'linear-gradient(135deg, oklch(0.62 0.16 220), oklch(0.55 0.20 270))' }}
            >MG</span>
            <div className="leading-tight">
              <div className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--ink-3)]">María G.</div>
              <div className="text-[12px] font-semibold">Mi semana</div>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="rounded-xl border border-[rgba(245,158,11,.3)] bg-[rgba(245,158,11,.06)] p-3 mb-3">
            <div className="flex items-start gap-2">
              <FiBell size={13} className="text-[#F59E0B] mt-0.5 flex-shrink-0" />
              <div className="text-[12px] leading-snug">
                <div className="font-semibold text-[var(--ink)]">5 fichajes pendientes</div>
                <div className="text-[var(--ink-3)] text-[11px]">Tu responsable los pre-rellenó. Confirma o corrige.</div>
              </div>
            </div>
          </div>
          <button className="w-full py-2.5 rounded-xl text-[12.5px] font-semibold text-white" style={{ background: '#0B0B0F' }}>
            Confirmar mi semana
          </button>
          <button className="w-full mt-2 py-2 rounded-xl text-[11.5px] font-medium border border-[var(--line)] text-[var(--ink-2)]">
            Revisar uno a uno
          </button>
        </div>
        <div className="px-4 py-2.5 border-t border-[var(--line)] flex items-center gap-1.5 text-[10.5px] font-mono uppercase tracking-wider bg-[var(--bg-2)]/40 text-[var(--ink-3)]">
          <FiShield size={11} /> Auditoría completa
        </div>
      </div>
    </div>
  )
}

/* ── Sections ────────────────────────────────────────────── */

function Nav({ audience, onAudienceToggle }) {
  const [solid, setSolid] = useState(false)
  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  return (
    <header
      className="fixed top-0 left-0 right-0 z-40 transition-all duration-300"
      style={{
        background: solid ? 'color-mix(in oklab, var(--bg) 80%, transparent)' : 'transparent',
        backdropFilter: solid ? 'blur(14px) saturate(140%)' : 'none',
        WebkitBackdropFilter: solid ? 'blur(14px) saturate(140%)' : 'none',
        borderBottom: solid ? '1px solid var(--line)' : '1px solid transparent',
      }}
    >
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 h-[64px] flex items-center justify-between">
        <a href="#top" className="flex items-center"><Logo size={26} /></a>
        <nav className="hidden md:flex items-center gap-1 text-[13.5px] text-[var(--ink-2)]">
          {[
            ['Funcionalidades', '#features'],
            ['Cómo funciona', '#how'],
            ['Precios', '#pricing'],
            ['Asesorías', '#asesorias'],
            ['Blog', '/blog'],
            ['Contacto', '#contacto'],
            ['FAQ', '#faq'],
          ].map(([l, h]) => (
            h.startsWith('/')
              ? <Link key={h} to={h} className="nav-link px-3 py-2 hover:text-[var(--ink)]">{l}</Link>
              : <a key={h} href={h} className="nav-link px-3 py-2 hover:text-[var(--ink)]">{l}</a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button
            onClick={onAudienceToggle}
            className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-medium border border-[var(--line)] text-[var(--ink-3)] hover:text-[var(--ink)] hover:border-[var(--line-2)]"
            title="Cambiar audiencia"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${audience === 'asesoria' ? 'bg-[#7C3AED]' : 'bg-[#2563EB]'}`} />
            {audience === 'asesoria' ? 'Soy asesoría' : 'Soy pyme'}
          </button>
          <Link to="/login" className="hidden sm:inline-block text-[13.5px] font-medium text-[var(--ink-2)] hover:text-[var(--ink)] px-3 py-2">Login</Link>
          <Link
            to="/registro?plan=avanzado"
            onClick={() => trackCta('nav', 'avanzado')}
            className="btn-primary !py-2.5 !px-4 !text-[13px]"
          >
            Prueba gratis <FiArrowRight size={13} />
          </Link>
        </div>
      </div>
    </header>
  )
}

function Hero({ audience }) {
  const isAse = audience === 'asesoria'
  const pymeHeadline = (
    <>Registro horario digital. <span className="font-display-i text-[var(--ink-3)]">Obligatorio por ley,</span> resuelto hoy.</>
  )
  const aseHeadline = (
    <>Una sola pantalla para <span className="font-display-i text-[var(--ink-3)]">todos los clientes</span> de tu cartera.</>
  )

  return (
    <section id="top" className="relative pt-[112px] sm:pt-[128px] pb-16 sm:pb-24">
      <div className="hero-halo" />
      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <span className="ui-chip border border-[var(--line)] bg-[var(--bg)]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse" />
              <span className="font-mono text-[10.5px] uppercase tracking-wider">Real Decreto · 2026</span>
              <span className="text-[var(--ink-3)]">·</span>
              <span>Fichaje digital obligatorio en España</span>
            </span>
            <Link to="/blog/real-decreto-2026-fichaje-digital" className="text-[12px] text-[var(--ink-3)] hover:text-[var(--ink)]">
              <FiArrowRight size={11} className="inline" /> Te explicamos qué cambia
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-12 gap-8 lg:gap-10 items-end">
          <div className="col-span-12 lg:col-span-7">
            <Reveal delay={50} key={`h1-${audience}`}>
              <h1 className="font-display text-[44px] sm:text-[64px] lg:text-[84px] leading-[0.98] tracking-[-0.02em] text-[var(--ink)]">
                {isAse ? aseHeadline : pymeHeadline}
              </h1>
            </Reveal>
            <Reveal delay={150} key={`p-${audience}`}>
              <p className="mt-7 text-[17px] sm:text-[19px] leading-[1.55] text-[var(--ink-2)] max-w-2xl">
                {isAse
                  ? <>Gestiona el control horario de toda tu cartera desde un único panel. Sin mínimo de empleados, sin permanencia, con comisión recurrente por cliente activo. Desde <span className="font-semibold text-[var(--ink)]">1,25&nbsp;€/empleado/mes</span>.</>
                  : <>Cumple con el registro horario obligatorio en España sin instalar nada, sin hardware, desde cualquier navegador. Setup en dos minutos · prueba 14 días sin tarjeta · desde <span className="font-semibold text-[var(--ink)]">1,25&nbsp;€/empleado/mes</span>.</>
                }
              </p>
            </Reveal>
            <Reveal delay={250}>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Link
                  to="/registro?plan=avanzado"
                  onClick={() => trackCta('hero', 'avanzado')}
                  className="btn-primary"
                >
                  Prueba gratis 14 días <FiArrowRight size={14} />
                </Link>
                <a href="#pricing" className="btn-ghost">Ver precios</a>
                <a href="#contacto" className="text-[13px] font-medium text-[var(--ink-2)] hover:text-[var(--ink)] inline-flex items-center gap-1.5">
                  <FiHeadphones size={13} /> Resuélvenos dudas antes
                </a>
                <span className="text-[12.5px] text-[var(--ink-3)] flex items-center gap-1.5 ml-1">
                  <FiCheck size={12} className="text-[#10B981]" /> Sin tarjeta · sin permanencia · cancela cuando quieras
                </span>
              </div>
            </Reveal>

            <Reveal delay={350}>
              <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
                {[
                  { k: '4 años', l: 'Archivo legal garantizado' },
                  { k: '<2 min', l: 'Setup desde el navegador' },
                  { k: <Counter to={50} suffix="+" />, l: 'Pymes ya fichan con nosotros' },
                ].map((m, i) => (
                  <div key={i}>
                    <div className="font-display text-3xl sm:text-4xl text-[var(--ink)]">{m.k}</div>
                    <div className="text-[11.5px] text-[var(--ink-3)] mt-1.5 leading-snug">{m.l}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-5 relative">
            <Reveal delay={400}>
              <div className="relative">
                <div style={{ transform: 'perspective(1600px) rotateY(-6deg) rotateX(2deg)' }}>
                  <AdminDashboardMockup compact={true} />
                </div>
                <div className="hidden md:flex absolute -left-4 top-10 ui-card px-3 py-2 items-center gap-2 text-[11.5px]">
                  <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                  <span className="font-medium">Cumplimiento legal</span>
                  <FiCheck size={12} className="text-[#10B981]" />
                </div>
                <div className="hidden md:flex absolute -right-3 bottom-10 ui-card px-3 py-2 items-center gap-2 text-[11.5px]">
                  <span className="w-7 h-7 rounded-md bg-[rgba(245,158,11,.12)] grid place-items-center"><FiAlertTriangle size={13} className="text-[#F59E0B]" /></span>
                  <div className="leading-tight">
                    <div className="font-medium">+12 horas extra</div>
                    <div className="text-[10px] text-[var(--ink-3)] font-mono">esta semana · revisar</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  )
}

function SocialProof() {
  return (
    <section className="border-y border-[var(--line)] bg-[var(--bg-2)]/40">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6">
        {[
          ['+50 pymes', 'ya fichan con PresenzIA', FiUsers],
          ['RGPD', 'compliant', FiShield],
          ['UE', 'Servidores en la Unión Europea', FiCloud],
          ['<2 min', 'Setup desde el navegador', FiZap],
        ].map(([k, v, I], i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg border border-[var(--line)] bg-[var(--bg)] grid place-items-center text-[var(--ink-2)]"><I size={14} /></span>
            <div className="leading-tight">
              <div className="text-[14px] font-semibold tnum">{k}</div>
              <div className="text-[11.5px] text-[var(--ink-3)]">{v}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function HowItWorks() {
  const steps = [
    { n: '01', kicker: 'Crea tu cuenta', title: 'En menos de dos minutos, sin tarjeta.', body: 'Registras tu empresa, eliges plan y entras al panel. Ningún hardware, ninguna app que instalar.', meta: 'Setup · 2 min' },
    { n: '02', kicker: 'Invita a tu equipo', title: 'Por email o por enlace. Fichan desde su móvil o el navegador.', body: 'Cada empleado recibe su acceso. Si quieres, configuras geofencing por centro de trabajo.', meta: 'Hasta 50 empleados · sin mínimo' },
    { n: '03', kicker: 'Recibe informes', title: 'Cumplimiento automático y archivo legal de 4 años.', body: 'Informes mensuales en PDF y Excel, listos para inspección. Conservación garantizada.', meta: 'PDF · Excel · API' },
  ]
  return (
    <section id="how" className="py-24 sm:py-32 border-b border-[var(--line)]">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeader
            num="§ 01"
            kicker="Cómo funciona"
            title={<>Tres pasos. <span className="font-display-i text-[var(--ink-3)]">Una tarde,</span> y vuelves a tu trabajo de verdad.</>}
            sub="Desde el alta hasta tener informes automáticos en tu bandeja, todo el flujo cabe en una mañana."
          />
        </Reveal>
        <div className="mt-16 grid-12">
          {steps.map((s, i) => (
            <Reveal key={i} delay={i * 100} className="col-span-12 md:col-span-4">
              <div className="h-full flex flex-col">
                <div className="flex items-baseline justify-between">
                  <div className="font-display text-[88px] leading-none text-[var(--ink)]/90">{s.n}</div>
                  <span className="font-mono text-[10.5px] uppercase tracking-wider text-[var(--ink-3)]">{s.meta}</span>
                </div>
                <div className="mt-3 hairline-grad" />
                <div className="mt-5 eyebrow">{s.kicker}</div>
                <h3 className="mt-3 font-display text-[28px] leading-[1.05] text-[var(--ink)]">{s.title}</h3>
                <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--ink-3)]">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

function UrgencyTimeline() {
  const events = [
    { y: '2019', t: 'RD-Ley 8/2019', d: 'Empieza la obligación de registrar la jornada de toda persona trabajadora en España.', tone: 'neutral' },
    { y: 'Hoy', t: 'Multas activas', d: 'De 751 € a 7.500 € por empleado. Un juicio por horas extra ronda los 12.000 €.', tone: 'amber' },
    { y: '2026', t: 'Decreto digital', d: 'El registro horario debe ser digital, accesible en tiempo real para Inspección y conservado 4 años.', tone: 'red' },
    { y: '→', t: 'Con PresenzIA', d: 'Cumples desde la primera tarde. Informes automáticos, archivo legal, sin instalar nada.', tone: 'brand' },
  ]
  const toneToColor = (t) => ({ neutral: 'var(--ink-3)', amber: '#F59E0B', red: '#EF4444', brand: '#7C3AED' }[t])
  return (
    <section className="py-24 sm:py-32 border-b border-[var(--line)] bg-[var(--bg-2)]/40">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeader
            num="§ 02"
            kicker="Cronología legal"
            title={<>Cada día sin fichaje digital es <span className="font-display-i text-[#C13030]">un día de exposición legal.</span></>}
            sub="La obligación lleva años aquí. Lo que cambia en 2026 es el formato: tu hoja de Excel deja de servir."
          />
        </Reveal>
        <div className="mt-14 relative">
          <div className="hidden md:block absolute left-0 right-0 top-[42px] h-px bg-[var(--line-2)]" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-6">
            {events.map((e, i) => (
              <Reveal key={i} delay={i * 120}>
                <div className="relative">
                  <div className="hidden md:flex items-center gap-3">
                    <span className="w-3.5 h-3.5 rounded-full ring-4 ring-[var(--bg-2)]" style={{ background: toneToColor(e.tone) }} />
                    <span className="font-mono text-[11px] uppercase tracking-wider" style={{ color: toneToColor(e.tone) }}>{e.tone === 'brand' ? 'AHORA' : e.y}</span>
                  </div>
                  <div className="md:hidden flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: toneToColor(e.tone) }} /><span className="font-mono text-[11px] uppercase tracking-wider">{e.y}</span></div>
                  <div className="font-display text-[40px] sm:text-[48px] leading-none mt-5 text-[var(--ink)]">{e.y === '→' ? <span style={{ color: '#7C3AED' }}>↳</span> : e.y}</div>
                  <div className="mt-3 font-semibold text-[15px]">{e.t}</div>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-[var(--ink-3)]">{e.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function RiskCalculator() {
  const [n, setN] = useState(12)
  const fmt = (v) => new Intl.NumberFormat('es-ES').format(Math.round(v))
  const minMulta = n * 751
  const maxMulta = n * 7500
  const annual = n * 1.25 * 12
  const ratio = (maxMulta / annual).toFixed(0)

  return (
    <section className="py-24 sm:py-32 border-b border-[var(--line)]">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <Eyebrow num="§ 03">Calculadora de riesgo</Eyebrow>
              <h2 className="font-display text-[40px] sm:text-[56px] leading-[1.02] mt-5">
                ¿Cuánto te juegas <span className="font-display-i text-[var(--ink-3)]">si te toca la inspección?</span>
              </h2>
              <p className="mt-5 text-[15px] leading-relaxed text-[var(--ink-3)] max-w-md">
                Mueve el deslizador para ver, en tiempo real, la diferencia entre lo que cuesta cumplir y lo que cuesta no cumplir. La cifra de la derecha es la sanción máxima vigente, multiplicada por tu plantilla.
              </p>
              <p className="mt-5 text-[13.5px] text-[var(--ink-2)]"><FiZap size={13} className="inline mr-1 text-[#7C3AED]" /> El software se paga solo con evitar una única inspección.</p>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <Reveal delay={100}>
              <div className="ui-card p-6 sm:p-8">
                <div className="flex items-end justify-between mb-2">
                  <div>
                    <div className="eyebrow">Tu plantilla</div>
                    <div className="font-display text-[64px] sm:text-[80px] leading-none mt-1 tnum">{n}</div>
                    <div className="text-[12.5px] text-[var(--ink-3)] -mt-1">empleados</div>
                  </div>
                  <div className="text-right">
                    <div className="eyebrow">Multa máx. potencial</div>
                    <div className="font-display text-[40px] sm:text-[48px] leading-none mt-1 text-[#C13030] tnum">{fmt(maxMulta)} €</div>
                    <div className="text-[12.5px] text-[var(--ink-3)] mt-1">{fmt(minMulta)} € mínimo · por inspección</div>
                  </div>
                </div>

                <input
                  type="range" min="2" max="50" value={n} step="1"
                  className="calc mt-6"
                  onChange={(e) => setN(Number(e.target.value))}
                  aria-label="Número de empleados"
                />
                <div className="flex justify-between text-[10.5px] font-mono text-[var(--ink-3)] mt-2">
                  <span>2</span><span>10</span><span>20</span><span>30</span><span>40</span><span>50</span>
                </div>

                <div className="mt-7 grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-[var(--line)] p-4">
                    <div className="eyebrow">Coste anual PresenzIA</div>
                    <div className="font-display text-[36px] mt-1 tnum">{fmt(annual)} €</div>
                    <div className="text-[11.5px] text-[var(--ink-3)] mt-0.5">{n} × 1,25 € × 12 meses</div>
                  </div>
                  <div className="rounded-xl p-4 ia-grad-bg text-white relative overflow-hidden">
                    <div className="text-[10px] font-mono uppercase tracking-wider opacity-80">Ratio coste/riesgo</div>
                    <div className="font-display text-[36px] mt-1 tnum">×{ratio}</div>
                    <div className="text-[11.5px] opacity-90 mt-0.5">La multa máxima multiplica por {ratio} el coste anual.</div>
                  </div>
                </div>

                <div className="mt-5 hairline-grad" />
                <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
                  <span className="text-[12.5px] text-[var(--ink-3)]">Un juicio medio por horas extra ronda los <span className="font-semibold text-[var(--ink)] tnum">12.000 €</span>.</span>
                  <Link
                    to="/registro?plan=avanzado"
                    onClick={() => trackCta('urgency', 'avanzado')}
                    className="btn-primary !py-2.5 !px-4 !text-[13px]"
                  >
                    Empezar gratis <FiArrowRight size={13} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  )
}

function LegalBlock() {
  return (
    <section className="py-24 sm:py-32 border-b border-[var(--line)] bg-[var(--bg-2)]/40">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-12 gap-8">
          <Reveal className="col-span-12 lg:col-span-5">
            <Eyebrow num="§ 04">El antes y el después</Eyebrow>
            <h2 className="font-display text-[40px] sm:text-[52px] leading-[1.02] mt-5">
              Excel y firmas en papel <span className="font-display-i text-[var(--ink-3)]">ya no cumplen.</span>
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-[var(--ink-3)] max-w-md">
              El nuevo Real Decreto exige que el registro sea digital, accesible en tiempo real para Inspección y conservado durante cuatro años.
            </p>
            <Link to="/blog/real-decreto-2026-fichaje-digital" className="mt-6 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-[var(--ink-2)] hover:text-[var(--ink)]">
              Lee el artículo completo <FiArrowUpRight size={13} />
            </Link>
          </Reveal>

          <div className="col-span-12 lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Reveal delay={100}>
              <div className="ui-card p-5 h-full relative">
                <div className="flex items-center justify-between">
                  <Pill tone="red"><FiX size={11} /> Antes · sin PresenzIA</Pill>
                  <span className="font-mono text-[10.5px] uppercase tracking-wider text-[var(--ink-3)]">RD 2026</span>
                </div>
                <ul className="mt-5 space-y-3 text-[13.5px]">
                  {[
                    ['Excel manual con firmas en papel', 'no auditable'],
                    ['Hojas que se pierden, se editan, se rehacen', 'riesgo legal'],
                    ['Multas de 751 € a 7.500 € por empleado', 'Inspección de Trabajo'],
                    ['Juicios por horas extra ~12.000 €', 'carga de prueba en la empresa'],
                  ].map(([t, m], i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-1 w-4 h-4 rounded-full bg-[rgba(239,68,68,.12)] grid place-items-center"><FiX size={10} className="text-[#C13030]" /></span>
                      <div>
                        <div className="text-[var(--ink-2)]">{t}</div>
                        <div className="font-mono text-[10.5px] uppercase tracking-wider text-[var(--ink-3)] mt-0.5">{m}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="ui-card p-5 h-full relative" style={{ background: 'linear-gradient(180deg, rgba(37,99,235,.04), transparent 40%)' }}>
                <div className="flex items-center justify-between">
                  <Pill tone="emerald"><FiCheck size={11} /> Después · con PresenzIA</Pill>
                  <span className="font-mono text-[10.5px] uppercase tracking-wider text-[var(--ink-3)]">RD 2026 ✓</span>
                </div>
                <ul className="mt-5 space-y-3 text-[13.5px]">
                  {[
                    ['Fichaje digital desde cualquier dispositivo', '100 % web'],
                    ['Archivo automático conservado 4 años', 'exportable a PDF / Excel'],
                    ['Informes listos para inspección en un clic', 'trazabilidad total'],
                    ['Cumplimiento del decreto digital 2026', 'antes que tu competencia'],
                  ].map(([t, m], i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="mt-1 w-4 h-4 rounded-full bg-[rgba(16,185,129,.15)] grid place-items-center"><FiCheck size={10} className="text-[#0F8C66]" /></span>
                      <div>
                        <div className="text-[var(--ink)]">{t}</div>
                        <div className="font-mono text-[10.5px] uppercase tracking-wider text-[var(--ink-3)] mt-0.5">{m}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  )
}

function FeaturesSummary() {
  const items = [
    { I: FiZap, kicker: 'Fichaje en un clic', title: 'Entrada, pausa y salida desde el navegador o el móvil.', tone: '#2563EB' },
    { I: FiShield, kicker: 'Cumplimiento garantizado', title: 'Archivo de 4 años, exportable a PDF y Excel para Inspección.', tone: '#10B981' },
    { I: FiBarChart2, kicker: 'Informes automáticos', title: 'Mensual, semanal o a medida — llegan a tu bandeja.', tone: '#7C3AED' },
    { I: FiSmartphone, kicker: '100 % móvil', title: 'Tu equipo ficha desde su teléfono, con o sin geolocalización.', tone: '#F97316' },
  ]
  return (
    <section className="py-24 sm:py-28 border-b border-[var(--line)]">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeader
            num="§ 05"
            kicker="Funcionalidades"
            title={<>Todo lo que necesitas. <span className="font-display-i text-[var(--ink-3)]">Y nada que sobre.</span></>}
          />
        </Reveal>
        <div className="grid grid-cols-12 gap-4 mt-14">
          {items.map((it, i) => (
            <Reveal key={i} delay={i * 80} className="col-span-12 sm:col-span-6 lg:col-span-3">
              <div className="ui-card p-5 h-full flex flex-col">
                <div className="flex items-center justify-between">
                  <span className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: `${it.tone}14`, color: it.tone }}>
                    <it.I size={16} />
                  </span>
                  <span className="font-mono text-[10.5px] uppercase tracking-wider text-[var(--ink-3)]">0{i + 1}</span>
                </div>
                <div className="mt-5 text-[12.5px] font-mono uppercase tracking-wider text-[var(--ink-3)]">{it.kicker}</div>
                <div className="mt-2 font-display text-[22px] leading-[1.15] text-[var(--ink)]">{it.title}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

function FeaturesDetailed() {
  const blocks = [
    {
      kicker: 'Fichaje rápido',
      title: <>Cinco segundos al empezar, <span className="font-display-i text-[var(--ink-3)]">cinco al terminar.</span></>,
      body: 'Tu equipo ficha entrada, pausa y salida desde el navegador o el móvil. Sin instalar nada. Sin tarjetas. Sin terminales en la pared.',
      bullets: ['Geofencing opcional por centro', 'Foto y huella biométrica del navegador (opcional)', 'Registro multi-turno y horario partido'],
      Mock: ClockMockup,
      side: 'right',
    },
    {
      kicker: 'Ausencias y vacaciones',
      title: <>Calendario que <span className="font-display-i text-[var(--ink-3)]">tu gestoría reconoce.</span></>,
      body: 'Vacaciones, médicos, asuntos propios y festivos por convenio en una sola vista. Aprobaciones por email. Saldo automático.',
      bullets: ['Festivos autonómicos y locales', 'Flujo de aprobación por responsable', 'Exportable al programa de nóminas'],
      Mock: CalendarMockup,
      side: 'left',
    },
    {
      kicker: 'Informes para inspección',
      title: <>Documentos listos <span className="font-display-i text-[var(--ink-3)]">antes de que te los pidan.</span></>,
      body: 'Cierre mensual automático, exportable a PDF y Excel. Conservación legal de cuatro años. Cumple con el RD-Ley 8/2019 y con el decreto digital de 2026.',
      bullets: ['Mensual, semanal o personalizado', 'Sello de tiempo y trazabilidad', 'Acceso de solo lectura para tu asesoría'],
      Mock: ReportMockup,
      side: 'right',
    },
    {
      kicker: 'Geolocalización',
      title: <>Saber quién ficha <span className="font-display-i text-[var(--ink-3)]">desde dónde.</span></>,
      body: 'Geofencing por centro de trabajo, fichajes en zona o fuera de zona, multi-centro. Solo plan Avanzado.',
      bullets: ['Radio configurable por centro', 'Pin verde en zona, naranja fuera', 'Alerta automática al responsable'],
      Mock: MapMockup,
      side: 'left',
    },
  ]

  return (
    <section id="features" className="py-24 sm:py-32 border-b border-[var(--line)] bg-[var(--bg-2)]/40">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 space-y-24 sm:space-y-32">
        {blocks.map((b, i) => (
          <div key={i} className="grid grid-cols-12 gap-8 lg:gap-14 items-center">
            <Reveal className={`col-span-12 lg:col-span-5 ${b.side === 'left' ? 'lg:order-2' : ''}`}>
              <Eyebrow num={`§ 0${6 + i}`}>{b.kicker}</Eyebrow>
              <h3 className="font-display text-[36px] sm:text-[48px] leading-[1.02] mt-5">{b.title}</h3>
              <p className="mt-5 text-[15px] leading-relaxed text-[var(--ink-3)] max-w-md">{b.body}</p>
              <ul className="mt-5 space-y-2.5 text-[13.5px]">
                {b.bullets.map((x, j) => (
                  <li key={j} className="flex items-start gap-2.5">
                    <span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-[var(--ink-2)]" />
                    <span className="text-[var(--ink-2)]">{x}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={150} className={`col-span-12 lg:col-span-7 ${b.side === 'left' ? 'lg:order-1' : ''}`}>
              <div><b.Mock /></div>
            </Reveal>
          </div>
        ))}
      </div>
    </section>
  )
}

function Kiosco() {
  return (
    <section className="py-24 sm:py-32 border-b border-[var(--line)] bg-[var(--bg-2)]/40">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-12 gap-8 lg:gap-14 items-center">
          <Reveal className="col-span-12 lg:col-span-5">
            <Eyebrow num="§ 10">Modo quiosco</Eyebrow>
            <h2 className="font-display text-[44px] sm:text-[60px] leading-[1.02] mt-5">
              Una tablet en la entrada, <span className="font-display-i text-[var(--ink-3)]">y todos fichan.</span>
            </h2>
            <p className="mt-6 text-[15px] sm:text-[16px] leading-relaxed text-[var(--ink-3)] max-w-md">
              Para equipos que no llevan móvil de empresa o tienen las manos ocupadas. Dejas una tablet o un PC fijo en la entrada del centro y cada empleado ficha en tres segundos: pulsa su nombre, introduce su PIN de cinco dígitos, listo.
            </p>
            <ul className="mt-7 space-y-2.5 text-[14px]">
              {[
                ['Sin móvil corporativo', 'Ideal para almacén, taller, hostelería, comercio u obra.'],
                ['Compartido sin riesgo', 'PIN personal por empleado y cierre de sesión automático.'],
                ['Multi-centro', 'Una tablet por centro, todas en el mismo panel.'],
                ['Hardware al gusto', 'Cualquier tablet Android/iPad o Windows reciente. Sin lectores ni terminales caros.'],
              ].map(([k, v], i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-1 w-4 h-4 rounded-full bg-[rgba(37,99,235,.12)] grid place-items-center"><FiCheck size={10} className="text-[#2563EB]" /></span>
                  <span className="text-[var(--ink-2)]"><strong className="text-[var(--ink)] font-semibold">{k}.</strong> {v}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-[rgba(16,185,129,.25)] bg-[rgba(16,185,129,.06)] text-[12.5px] text-[#0F8C66]">
              <FiZap size={12} /> Te dejamos lista la primera tablet sin coste
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a href="#contacto" className="btn-ghost"><FiTablet size={13} /> Pregúntanos cómo</a>
              <Link to="/registro?plan=avanzado" className="text-[13px] font-mono text-[var(--ink-3)] hover:text-[var(--ink)]">Plan Avanzado</Link>
            </div>
          </Reveal>
          <Reveal delay={150} className="col-span-12 lg:col-span-7">
            <KioscoMockup />
          </Reveal>
        </div>
      </div>
    </section>
  )
}

function FichajeAsistido() {
  return (
    <section className="py-24 sm:py-32 border-b border-[var(--line)]">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-12 gap-8 lg:gap-14 items-center">
          <Reveal className="col-span-12 lg:col-span-7 lg:order-2">
            <Eyebrow num="§ 11">Implantación sin resistencia</Eyebrow>
            <h2 className="font-display text-[44px] sm:text-[60px] leading-[1.02] mt-5">
              Para equipos que <span className="font-display-i text-[var(--ink-3)]">no quieren fichar.</span>
            </h2>
            <p className="mt-6 text-[15px] sm:text-[16px] leading-relaxed text-[var(--ink-3)] max-w-xl">
              Si has intentado implantar un fichaje y te has chocado con un equipo que se resiste, esto es para ti. El responsable rellena la planilla semanal en base al turno acordado y cada empleado la confirma con un clic al final de semana o mes. Sin pelea, sin formación, sin friccionar la jornada.
            </p>
            <ul className="mt-7 space-y-2.5 text-[14px] max-w-xl">
              {[
                ['Pre-rellenado por el responsable', 'En segundos, sobre el horario y el grupo que ya tenías definidos.'],
                ['Confirmación del empleado en un clic', '"Confirmar mi semana" o revisión día a día. Lo eliges tú.'],
                ['Trazabilidad completa', 'Queda registrado quién pre-rellenó y quién confirmó. Cumple el RD-Ley 8/2019.'],
                ['Transición progresiva', 'Cuando el equipo se acostumbra, pasan al fichaje propio sin esfuerzo añadido.'],
              ].map(([k, v], i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-1 w-4 h-4 rounded-full bg-[rgba(124,58,237,.12)] grid place-items-center"><FiCheck size={10} className="text-[#7C3AED]" /></span>
                  <span className="text-[var(--ink-2)]"><strong className="text-[var(--ink)] font-semibold">{k}.</strong> {v}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-[rgba(124,58,237,.25)] bg-[rgba(124,58,237,.06)] text-[12.5px] text-[#7C3AED]">
              <FiZap size={12} /> Implantas el fichaje en una semana sin discutir con la plantilla
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a href="#contacto" className="btn-ghost"><FiUsers size={13} /> Cuéntanos tu caso</a>
              <Link to="/registro?plan=avanzado" className="text-[13px] font-mono text-[var(--ink-3)] hover:text-[var(--ink)]">Plan Avanzado</Link>
            </div>
          </Reveal>
          <Reveal delay={150} className="col-span-12 lg:col-span-5 lg:order-1">
            <FichajeAsistidoMockup />
          </Reveal>
        </div>
      </div>
    </section>
  )
}

function ProductTour() {
  const tabs = [
    { id: 'admin', label: 'Panel admin', desc: 'Vista de gerente: fichajes en vivo, KPIs y horas de la semana.', Mock: AdminDashboardMockup },
    { id: 'cal', label: 'Ausencias', desc: 'Calendario mensual con vacaciones, festivos y aprobaciones.', Mock: CalendarMockup },
    { id: 'rep', label: 'Informe', desc: 'Cierre mensual exportable a PDF y Excel, listo para Inspección.', Mock: ReportMockup },
    { id: 'mob', label: 'App móvil', desc: 'El empleado ficha desde el navegador móvil. Sin descargas.', Mock: MobileMockup },
  ]
  const [active, setActive] = useState('admin')
  const cur = tabs.find(t => t.id === active)
  return (
    <section className="py-24 sm:py-32 border-b border-[var(--line)]">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <Reveal className="max-w-xl">
            <Eyebrow num="§ 12">Tour del producto</Eyebrow>
            <h2 className="font-display text-[40px] sm:text-[56px] leading-[1.02] mt-5">
              Una herramienta sin <span className="font-display-i text-[var(--ink-3)]">páginas escondidas.</span>
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <div className="flex flex-wrap gap-1.5 p-1 rounded-full border border-[var(--line)] bg-[var(--bg)]">
              {tabs.map(t => (
                <button
                  key={t.id}
                  onClick={() => setActive(t.id)}
                  className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-medium transition-colors ${active === t.id ? 'bg-[var(--ink)] text-[var(--bg)]' : 'text-[var(--ink-2)] hover:text-[var(--ink)]'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="grid grid-cols-12 gap-8 items-start">
          <Reveal className="col-span-12 lg:col-span-4" key={cur.id}>
            <div className="font-display text-[32px] leading-[1.05]">{cur.label}</div>
            <p className="mt-3 text-[14.5px] leading-relaxed text-[var(--ink-3)] max-w-sm">{cur.desc}</p>
            <div className="mt-6 hairline-grad" />
            <div className="mt-5 space-y-2.5 text-[13.5px]">
              {[
                ['Tiempo real', 'fichajes en vivo · sin recargar'],
                ['Roles y permisos', 'admin · responsable · empleado'],
                ['Multi-idioma', 'ES · CA · EN · GL · EU'],
              ].map(([k, v], i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-[var(--ink-2)]">{k}</span>
                  <span className="font-mono text-[10.5px] uppercase tracking-wider text-[var(--ink-3)]">{v}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120} className="col-span-12 lg:col-span-8" key={'m-' + cur.id}>
            <div><cur.Mock /></div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

function Sectors() {
  const sectors = [
    'Asesorías laborales',
    'Despachos de abogados',
    'Comercios',
    'Climatización',
    'Suministro eléctrico',
    'Ferreterías industriales',
    'Calzado',
    'Seguridad contra incendios',
  ]
  const row = [...sectors, ...sectors]
  return (
    <section className="py-20 sm:py-24 border-b border-[var(--line)] bg-[var(--bg-2)]/40">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="flex items-baseline justify-between flex-wrap gap-4">
            <Eyebrow num="§ 13">Sectores</Eyebrow>
            <span className="text-[12.5px] text-[var(--ink-3)]">Adaptado al convenio de cada gremio</span>
          </div>
          <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.05] mt-4 max-w-3xl">
            Funcionando en pymes que <span className="font-display-i text-[var(--ink-3)]">no se parecen entre sí.</span>
          </h2>
        </Reveal>
        <Reveal delay={120}>
          <div className="mt-10 overflow-hidden relative" style={{ maskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)' }}>
            <div className="marquee-track">
              {row.map((s, i) => (
                <div key={i} className="flex items-center gap-3 whitespace-nowrap">
                  <span className="font-display text-[28px] sm:text-[36px] text-[var(--ink)]">{s}</span>
                  <span className="font-mono text-[12px] text-[var(--ink-3)]">/ /</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

function Testimonials() {
  const items = [
    {
      q: 'Llevábamos años con un Excel que firmaba todo el mundo. La inspección nos asustaba. Con PresenzIA tardé una mañana en pasar a 18 personas y ahora tengo el cierre del mes en automático.',
      name: 'Rosa Fernández',
      role: 'Gerente · Climatización Mediterráneo',
      hue: 220,
      i: 'RF',
    },
    {
      q: 'Gestionamos el control horario de 23 clientes desde el panel multi-empresa. La comisión por cliente activo cubre dos sueldos. Para una asesoría pequeña como la nuestra, esto es un antes y un después.',
      name: 'Javier Morales',
      role: 'Socio · Asesoría Morales & Asociados',
      hue: 270,
      i: 'JM',
    },
    {
      q: 'Mis empleados fichan desde el móvil en obra. La geolocalización nos ahorra discusiones. Y el coste, comparado con lo que pagaba antes a otro proveedor, es ridículo.',
      name: 'Miguel Ángel Torres',
      role: 'Propietario · Ferretería Industrial Torres',
      hue: 30,
      i: 'MT',
    },
  ]
  return (
    <section className="py-24 sm:py-32 border-b border-[var(--line)]">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeader
            num="§ 14"
            kicker="Testimonios"
            title={<>Lo que cuentan <span className="font-display-i text-[var(--ink-3)]">quienes ya cumplen.</span></>}
          />
        </Reveal>
        <div className="grid grid-cols-12 gap-5 mt-14">
          {items.map((t, i) => (
            <Reveal key={i} delay={i * 100} className="col-span-12 md:col-span-4">
              <figure className="ui-card p-6 h-full flex flex-col">
                <blockquote className="font-display text-[22px] leading-[1.25] text-[var(--ink)] flex-1">
                  <span className="text-[var(--ink-3)]">“</span>{t.q}<span className="text-[var(--ink-3)]">”</span>
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-[var(--line)] flex items-center gap-3">
                  <AvatarInitial initials={t.i} hue={t.hue} size={36} />
                  <div className="leading-tight">
                    <div className="text-[13px] font-semibold">{t.name}</div>
                    <div className="text-[11.5px] text-[var(--ink-3)]">{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

function ServiciosContacto() {
  const servicios = [
    {
      I: FiZap,
      title: 'Onboarding asistido',
      body: 'Una videollamada con nosotros: configuramos contigo centros, empleados, calendarios y turnos. Te dejamos lista la cuenta para empezar a fichar.',
      tag: 'Sin coste',
      tone: '#10B981',
    },
    {
      I: FiTablet,
      title: 'Instalación de tablet quiosco',
      body: 'Te guiamos por remoto para dejar tu tablet o PC fijo del centro listo como quiosco compartido. Empleados, PIN y centro asociado configurados.',
      tag: 'Gratis · 1 tablet incluida',
      tone: '#2563EB',
    },
    {
      I: FiFile,
      title: 'Migración desde Excel o papel',
      body: 'Volcamos tus registros históricos para que tengas el archivo legal de cuatro años desde el día uno. Servicio bajo presupuesto según volumen.',
      tag: 'Servicio con coste',
      tone: '#7C3AED',
    },
    {
      I: FiHeadphones,
      title: 'Soporte humano en castellano',
      body: 'Atención por teléfono y correo durante la implantación y después. Respondemos el mismo día laborable.',
      tag: 'Incluido siempre',
      tone: '#F97316',
    },
  ]
  return (
    <section id="contacto" className="py-24 sm:py-32 border-b border-[var(--line)]">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeader
            num="§ 15"
            kicker="Implantación y soporte"
            title={<>Empezar es fácil <span className="font-display-i text-[var(--ink-3)]">si no estás solo.</span></>}
          />
        </Reveal>

        <div className="grid grid-cols-12 gap-6 mt-14">
          {/* Servicios */}
          <div className="col-span-12 lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {servicios.map((s, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="ui-card p-5 h-full flex flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <span className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: `${s.tone}14`, color: s.tone }}>
                      <s.I size={17} />
                    </span>
                    <span className="text-[10.5px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border border-[var(--line)] text-[var(--ink-3)]">{s.tag}</span>
                  </div>
                  <h3 className="mt-4 font-display text-[20px] leading-[1.15] text-[var(--ink)]">{s.title}</h3>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-[var(--ink-3)] flex-1">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Tarjeta contacto */}
          <Reveal delay={300} className="col-span-12 lg:col-span-5">
            <div className="ui-card p-7 h-full flex flex-col bg-gradient-to-br from-[var(--bg)] to-[var(--bg-2)]/60">
              <Pill tone="emerald"><FiHelpCircle size={11} /> Te atendemos antes de comprar</Pill>
              <h3 className="mt-5 font-display text-[28px] sm:text-[32px] leading-[1.05]">
                ¿Dudas? <span className="font-display-i text-[var(--ink-3)]">Te las resolvemos.</span>
              </h3>
              <p className="mt-3 text-[14px] leading-relaxed text-[var(--ink-3)]">
                Llámanos o escríbenos y un especialista te explica todo lo que necesites antes de empezar. Sin compromiso, sin guion comercial.
              </p>

              <div className="mt-6 space-y-3">
                <a
                  href="tel:+34966534753"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[var(--line)] hover:border-[var(--line-2)] hover:bg-[var(--bg-2)]/40 transition-colors"
                >
                  <span className="w-10 h-10 rounded-lg bg-[rgba(37,99,235,.12)] grid place-items-center text-[#2563EB]"><FiPhone size={16} /></span>
                  <div className="leading-tight flex-1">
                    <div className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--ink-3)]">Teléfono</div>
                    <div className="text-[16px] font-semibold tnum">+34 966 53 47 53</div>
                  </div>
                  <FiArrowUpRight size={14} className="text-[var(--ink-3)]" />
                </a>

                <a
                  href="mailto:info@presenzia.app"
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[var(--line)] hover:border-[var(--line-2)] hover:bg-[var(--bg-2)]/40 transition-colors"
                >
                  <span className="w-10 h-10 rounded-lg bg-[rgba(124,58,237,.12)] grid place-items-center text-[#7C3AED]"><FiMail size={16} /></span>
                  <div className="leading-tight flex-1">
                    <div className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--ink-3)]">Correo</div>
                    <div className="text-[15px] font-semibold">info@presenzia.app</div>
                  </div>
                  <FiArrowUpRight size={14} className="text-[var(--ink-3)]" />
                </a>
              </div>

              <div className="mt-6 pt-5 border-t border-[var(--line)] flex items-start gap-3 text-[12.5px]">
                <FiClock size={14} className="text-[var(--ink-3)] mt-0.5 flex-shrink-0" />
                <div className="leading-relaxed text-[var(--ink-2)]">
                  <strong className="font-semibold text-[var(--ink)]">Horario de atención</strong><br />
                  Lunes a jueves · 8:00 a 18:00<br />
                  Viernes · 8:00 a 14:00
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  )
}

function Asesorias() {
  return (
    <section id="asesorias" className="py-24 sm:py-32 border-b border-[var(--line)] relative overflow-hidden">
      <div className="absolute inset-0 mesh-soft pointer-events-none" />
      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-12 gap-8 lg:gap-14 items-center">
          <Reveal className="col-span-12 lg:col-span-5">
            <Pill tone="purple"><FiBriefcase size={11} /> Para asesorías laborales</Pill>
            <h2 className="font-display text-[44px] sm:text-[60px] leading-[1.02] mt-5">
              Una sola pantalla <span className="font-display-i text-[var(--ink-3)]">para toda tu cartera.</span>
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-[var(--ink-3)] max-w-md">
              Gestiona el control horario de todos tus clientes desde un único panel multi-empresa. Comisión recurrente por cada cliente activo. Soporte directo para asesorías.
            </p>
            <ul className="mt-6 space-y-2.5 text-[14px]">
              {[
                'Panel multi-empresa con cambio rápido entre clientes',
                'Comisión recurrente por cliente activo',
                'Acceso de solo lectura para tu equipo',
                'Onboarding asistido para los primeros 5 clientes',
              ].map((x, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-1 w-4 h-4 rounded-full bg-[rgba(124,58,237,.12)] grid place-items-center"><FiCheck size={10} className="text-[#7C3AED]" /></span>
                  <span className="text-[var(--ink-2)]">{x}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a href="mailto:asesorias@presenzia.app" className="btn-primary">Hablar con el equipo asesorías <FiArrowRight size={13} /></a>
              <a href="mailto:asesorias@presenzia.app" className="text-[13px] font-mono text-[var(--ink-3)] hover:text-[var(--ink)]">asesorias@presenzia.app</a>
            </div>
          </Reveal>
          <Reveal delay={150} className="col-span-12 lg:col-span-7">
            <MultiCompanyMockup />
          </Reveal>
        </div>
      </div>
    </section>
  )
}

function Pricing() {
  const [annual, setAnnual] = useState(true)
  const proPrice = annual ? '1,25' : '1,50'
  const advPrice = annual ? '2,95' : '3,50'

  return (
    <section id="pricing" className="py-24 sm:py-32 border-b border-[var(--line)] bg-[var(--bg-2)]/40">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col items-center text-center">
          <Reveal>
            <Eyebrow num="§ 16">Precios</Eyebrow>
            <h2 className="font-display text-[44px] sm:text-[64px] leading-[1.02] mt-5 max-w-3xl">
              Sin mínimo de empleados. <span className="font-display-i text-[var(--ink-3)]">Sin permanencia.</span>
            </h2>
            <p className="mt-5 text-[15px] text-[var(--ink-3)] max-w-xl">
              Empieza con 14 días gratis sin tarjeta. Cambia de plan o cancela cuando quieras.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <div className="mt-8 inline-flex p-1 rounded-full border border-[var(--line)] bg-[var(--bg)]">
              <button onClick={() => setAnnual(false)} className={`px-4 py-1.5 rounded-full text-[12.5px] font-medium ${!annual ? 'bg-[var(--ink)] text-[var(--bg)]' : 'text-[var(--ink-3)]'}`}>Mensual</button>
              <button onClick={() => setAnnual(true)} className={`px-4 py-1.5 rounded-full text-[12.5px] font-medium ${annual ? 'bg-[var(--ink)] text-[var(--bg)]' : 'text-[var(--ink-3)]'}`}>
                Anual <span className="ml-1 text-[10px] font-mono opacity-70">−2 meses</span>
              </button>
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-12 gap-5">
          {/* Pro */}
          <Reveal className="col-span-12 md:col-span-6">
            <div className="ui-card p-7 h-full flex flex-col">
              <div className="flex items-center justify-between">
                <div className="font-display text-[28px]">Pro</div>
                <Pill tone="brand">Recomendado para empezar</Pill>
              </div>
              <div className="mt-5 flex items-end gap-2">
                <span className="font-display text-[72px] leading-none tnum">{proPrice} €</span>
                <span className="text-[12.5px] text-[var(--ink-3)] mb-3">/empleado/mes<br />{annual ? 'facturado anual' : 'facturado mensual'}</span>
              </div>
              <ul className="mt-6 space-y-2.5 text-[13.5px] flex-1">
                {[
                  'Fichaje web + móvil',
                  'Ausencias y vacaciones',
                  'Informes mensuales en PDF y Excel',
                  'Archivo legal de 4 años',
                  'Soporte por email',
                ].map((x, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <FiCheck size={13} className="mt-1 text-[#0F8C66]" />
                    <span className="text-[var(--ink-2)]">{x}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/registro?plan=pro"
                onClick={() => trackCta('pricing-pro', 'pro')}
                className="btn-ghost mt-6 justify-center"
              >
                Empezar con Pro <FiArrowRight size={13} />
              </Link>
            </div>
          </Reveal>

          {/* Avanzado */}
          <Reveal delay={120} className="col-span-12 md:col-span-6">
            <div className="ui-card p-7 h-full flex flex-col pricing-highlight relative overflow-hidden">
              <div className="absolute -top-12 -right-12 w-60 h-60 rounded-full" style={{ background: 'radial-gradient(circle, rgba(147,51,234,.4), transparent 65%)' }} />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="font-display text-[28px]">Avanzado</div>
                  <span className="ui-chip" style={{ background: 'rgba(255,255,255,.12)', color: '#fff' }}><FiZap size={11} /> Más elegido</span>
                </div>
              </div>
              <div className="relative mt-5 flex items-end gap-2">
                <span className="font-display text-[72px] leading-none tnum">{advPrice} €</span>
                <span className="text-[12.5px] opacity-70 mb-3">/empleado/mes<br />{annual ? 'facturado anual' : 'facturado mensual'}</span>
              </div>
              <ul className="relative mt-6 space-y-2.5 text-[13.5px] flex-1">
                {[
                  'Todo lo de Pro',
                  'Geolocalización con geofencing',
                  'Multi-centro de trabajo',
                  'Horas extra avanzadas y compensaciones',
                  'Soporte prioritario y SLA',
                ].map((x, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <FiCheck size={13} className="mt-1 text-[#7AE6BC]" />
                    <span className="opacity-95">{x}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/registro?plan=avanzado"
                onClick={() => trackCta('pricing-avanzado', 'avanzado')}
                className="relative mt-6 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-semibold text-[14px]"
                style={{ background: '#FAFAF7', color: '#0B0B0F' }}
              >
                Prueba 14 días gratis <FiArrowRight size={13} />
              </Link>
            </div>
          </Reveal>
        </div>

        <Reveal delay={200}>
          <p className="mt-8 text-center text-[12.5px] text-[var(--ink-3)] max-w-xl mx-auto">
            Todos los planes incluyen RGPD, servidores en la UE y exportaciones para tu asesoría. Sin coste de implantación.
          </p>
        </Reveal>
      </div>
    </section>
  )
}

const FAQS = [
  {
    q: '¿Es realmente obligatorio fichar en mi empresa?',
    a: 'Sí. Desde 2019 el RD-Ley 8/2019 obliga a registrar la jornada de toda persona trabajadora en España, sin excepción de tamaño. El nuevo Real Decreto de 2026 añade que el registro debe ser digital, accesible en tiempo real y conservado durante cuatro años.',
  },
  {
    q: '¿Cómo funciona la prueba gratuita?',
    a: '14 días con todas las funciones del plan Avanzado, sin pedir tarjeta. Si al terminar no quieres seguir, simplemente no se factura nada.',
  },
  {
    q: '¿Puedo cambiar de plan más adelante?',
    a: 'Cuando quieras, en cualquier dirección. Subes a Avanzado para activar geolocalización y multi-centro, o bajas a Pro si no las necesitas.',
  },
  {
    q: '¿Cómo protegéis los datos? ¿Cumple el RGPD?',
    a: 'Sí. Los datos viajan cifrados en tránsito y en reposo, los servidores están en la Unión Europea y firmamos contrato de encargado de tratamiento. Tu asesoría puede tener acceso de solo lectura.',
  },
  {
    q: '¿Funciona en móvil y tablet? ¿Puedo dejar una tablet fija para el equipo?',
    a: 'Sí. Tus empleados pueden fichar desde su móvil o, si prefieres, dejas una tablet (o un PC fijo) en la entrada del centro como modo quiosco compartido y fichan con su PIN personal en tres segundos. Te ayudamos sin coste a dejar lista la primera tablet.',
  },
  {
    q: '¿Qué métodos de pago aceptáis?',
    a: 'Tarjeta y SEPA. Facturación mensual o anual. Anual incluye dos meses gratis.',
  },
  {
    q: '¿Hay un mínimo de empleados?',
    a: 'No. Aceptamos desde dos empleados, sin importe mínimo. La mayoría de competidores piden cinco o diez.',
  },
  {
    q: 'Soy asesoría — ¿hay condiciones especiales?',
    a: 'Sí. Tienes panel multi-empresa, comisión recurrente por cliente activo y soporte directo. Escríbenos a asesorias@presenzia.app.',
  },
]

function FAQ() {
  return (
    <section id="faq" className="py-24 sm:py-32 border-b border-[var(--line)]">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 grid grid-cols-12 gap-8">
        <Reveal className="col-span-12 lg:col-span-4">
          <Eyebrow num="§ 17">Preguntas frecuentes</Eyebrow>
          <h2 className="font-display text-[40px] sm:text-[52px] leading-[1.02] mt-5">
            Lo que casi todo el mundo <span className="font-display-i text-[var(--ink-3)]">pregunta primero.</span>
          </h2>
          <p className="mt-5 text-[14.5px] text-[var(--ink-3)] max-w-sm">
            ¿Tu duda no está? Escríbenos a <a className="underline underline-offset-4 hover:text-[var(--ink)]" href="mailto:info@presenzia.app">info@presenzia.app</a> y respondemos el mismo día laborable.
          </p>
        </Reveal>
        <div className="col-span-12 lg:col-span-8 divide-y divide-[var(--line)] border-y border-[var(--line)]">
          {FAQS.map((it, i) => (
            <Reveal key={i} delay={i * 30}>
              <details className="group py-5">
                <summary className="flex items-start justify-between gap-6">
                  <div className="flex-1">
                    <span className="font-mono text-[10.5px] uppercase tracking-wider text-[var(--ink-3)]">0{i + 1}</span>
                    <div className="mt-1.5 font-display text-[22px] leading-[1.2] text-[var(--ink)]">{it.q}</div>
                  </div>
                  <span className="faq-arrow mt-2 transition-transform duration-200 text-[var(--ink-2)]">
                    <FiPlus size={18} />
                  </span>
                </summary>
                <p className="mt-3 text-[14px] leading-relaxed text-[var(--ink-2)] max-w-2xl">{it.a}</p>
              </details>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

function BlogTeaser() {
  // Tomamos los 3 más recientes (blogPosts ya está ordenado por fecha desc en el array).
  const posts = blogPosts.slice(0, 3)
  return (
    <section className="relative">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div className="max-w-2xl">
              <Eyebrow>Recursos</Eyebrow>
              <h2 className="font-display text-[34px] sm:text-[48px] leading-[1.02] mt-4 tracking-[-0.02em]">
                Lo último del blog. <span className="font-display-i text-[var(--ink-3)]">Sin letra pequeña.</span>
              </h2>
              <p className="mt-4 text-[15px] text-[var(--ink-3)]">
                Guías, análisis normativo y casos prácticos sobre el registro de jornada en España.
              </p>
            </div>
            <Link
              to="/blog"
              className="btn-ghost text-[13.5px]"
              onClick={() => {
                if (typeof window !== 'undefined' && window.gtag) {
                  window.gtag('event', 'blog_teaser_click', { ubicacion: 'landing_ver_todos' })
                }
              }}
            >
              Ver todos los artículos <FiArrowRight size={13} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {posts.map((p) => (
            <Reveal key={p.slug} delay={50}>
              <Link
                to={`/blog/${p.slug}`}
                onClick={() => {
                  if (typeof window !== 'undefined' && window.gtag) {
                    window.gtag('event', 'blog_teaser_click', { ubicacion: 'landing_card', slug: p.slug })
                  }
                }}
                className="group block h-full p-6 rounded-2xl border border-[var(--line)] bg-[var(--bg)] hover:border-[var(--line-2)] hover:shadow-md transition-all"
              >
                <div className="text-[10.5px] font-mono uppercase tracking-wider text-[var(--ink-3)] mb-3">
                  {p.category}
                </div>
                <h3 className="text-[17px] font-semibold leading-snug mb-3 group-hover:text-[#2563EB] transition-colors">
                  {p.title}
                </h3>
                <p className="text-[13.5px] text-[var(--ink-3)] line-clamp-3 mb-5 leading-relaxed">
                  {p.excerpt}
                </p>
                <div className="flex items-center gap-3 text-[11.5px] text-[var(--ink-3)]">
                  <span className="inline-flex items-center gap-1"><FiCalendar size={11} /> {p.dateDisplay}</span>
                  <span className="inline-flex items-center gap-1"><FiClock size={11} /> {p.readMinutes} min</span>
                  <span className="ml-auto text-[#2563EB] font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Leer <FiArrowRight size={11} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  )
}

function FinalCTA() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 mesh-soft" />
      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <div className="text-center max-w-4xl mx-auto">
            <Eyebrow num="§ 18">Última llamada</Eyebrow>
            <h2 className="font-display text-[44px] sm:text-[80px] leading-[0.98] mt-6 tracking-[-0.02em]">
              Cada día sin fichaje digital <br className="hidden sm:block" />
              <span className="font-display-i text-[var(--ink-3)]">es un día de exposición legal.</span>
            </h2>
            <p className="mt-6 text-[15px] sm:text-[17px] text-[var(--ink-3)] max-w-xl mx-auto">
              Empieza hoy. Tarda dos minutos. No hace falta tarjeta. Lo demás, ya está hecho.
            </p>
            <div className="mt-9 flex flex-wrap justify-center items-center gap-3">
              <Link
                to="/registro?plan=avanzado"
                onClick={() => trackCta('final_cta', 'avanzado')}
                className="btn-primary"
              >
                Prueba gratis 14 días <FiArrowRight size={14} />
              </Link>
              <a href="#pricing" className="btn-ghost">Ver precios</a>
            </div>
            <div className="mt-6 text-[12px] font-mono text-[var(--ink-3)] uppercase tracking-wider">
              <FiCheck size={11} className="inline mr-1.5 text-[#0F8C66]" /> Sin tarjeta · sin permanencia · cancela cuando quieras
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="border-t border-[var(--line)] bg-[var(--bg-2)]/60">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-14">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <Logo size={26} />
            <p className="mt-4 text-[13.5px] text-[var(--ink-3)] max-w-sm">
              Control horario y fichaje digital para pymes y asesorías. Cumple con el registro horario obligatorio en España, sin instalar nada.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="ui-chip">RGPD</span>
              <span className="ui-chip">Servidores UE</span>
              <span className="ui-chip">RD-Ley 8/2019</span>
              <span className="ui-chip">Decreto 2026</span>
            </div>
          </div>
          <div className="col-span-6 md:col-span-2">
            <div className="eyebrow">Producto</div>
            <ul className="mt-4 space-y-2 text-[13.5px]">
              <li><a href="#features" className="text-[var(--ink-2)] hover:text-[var(--ink)]">Funcionalidades</a></li>
              <li><a href="#pricing" className="text-[var(--ink-2)] hover:text-[var(--ink)]">Precios</a></li>
              <li><a href="#asesorias" className="text-[var(--ink-2)] hover:text-[var(--ink)]">Asesorías</a></li>
              <li><a href="#contacto" className="text-[var(--ink-2)] hover:text-[var(--ink)]">Contacto y soporte</a></li>
              <li><Link to="/blog" className="text-[var(--ink-2)] hover:text-[var(--ink)]">Blog</Link></li>
            </ul>
          </div>
          <div className="col-span-6 md:col-span-2">
            <div className="eyebrow">Legal</div>
            <ul className="mt-4 space-y-2 text-[13.5px]">
              <li><Link to="/aviso-legal" className="text-[var(--ink-2)] hover:text-[var(--ink)]">Aviso legal</Link></li>
              <li><Link to="/privacidad" className="text-[var(--ink-2)] hover:text-[var(--ink)]">Privacidad</Link></li>
              <li><Link to="/cookies" className="text-[var(--ink-2)] hover:text-[var(--ink)]">Cookies</Link></li>
            </ul>
          </div>
          <div className="col-span-12 md:col-span-3">
            <div className="eyebrow">Contacto</div>
            <ul className="mt-4 space-y-2 text-[13.5px]">
              <li>
                <a href="tel:+34966534753" className="text-[var(--ink-2)] hover:text-[var(--ink)] inline-flex items-center gap-1.5 font-mono tnum">
                  <FiPhone size={11} /> +34 966 53 47 53
                </a>
              </li>
              <li className="font-mono"><a href="mailto:info@presenzia.app" className="text-[var(--ink-2)] hover:text-[var(--ink)] inline-flex items-center gap-1.5"><FiMail size={11} /> info@presenzia.app</a></li>
              <li className="font-mono"><a href="mailto:asesorias@presenzia.app" className="text-[var(--ink-2)] hover:text-[var(--ink)]">asesorias@presenzia.app</a></li>
              <li className="font-mono"><a href="mailto:privacidad@presenzia.app" className="text-[var(--ink-2)] hover:text-[var(--ink)]">privacidad@presenzia.app <span className="not-italic text-[10px] text-[var(--ink-3)] font-sans normal-case">(DPO)</span></a></li>
              <li className="text-[11.5px] text-[var(--ink-3)] mt-3 leading-relaxed">
                Lun-Jue 8-18 · Vie 8-14
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-[var(--line)] flex flex-wrap items-center justify-between gap-3 text-[12px] text-[var(--ink-3)]">
          <span>© 2026 PresenzIA · Todos los derechos reservados</span>
          <span className="font-mono uppercase tracking-wider text-[10.5px]">Hecho en España · v2.0</span>
        </div>
      </div>
    </footer>
  )
}

/* ── Página principal ────────────────────────────────────── */

export default function LandingPageV2() {
  const [audience, setAudience] = useState('pyme')
  const toggleAudience = () => setAudience(a => a === 'pyme' ? 'asesoria' : 'pyme')

  return (
    <>
      <Helmet>
        <title>Control Horario para Pymes desde 1,25 € | PresenzIA</title>
        <meta name="description" content="Software de control horario para pymes desde 1,25 €/empleado/mes (pago anual). Cumple el registro horario obligatorio. Sin mínimos. Prueba gratis 14 días sin tarjeta." />
        <meta name="keywords" content="control horario, fichaje digital, registro jornada laboral, control presencia empleados, software fichaje pymes, RD-ley 8/2019, app fichaje" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="theme-color" content="#0B0B0F" />
        <meta name="geo.region" content="ES" />
        <link rel="canonical" href="https://presenzia.app/landing" />
        {/* Open Graph (Facebook, LinkedIn, WhatsApp, Slack) */}
        <meta property="og:title" content="Control Horario para Pymes desde 1,25 € | PresenzIA" />
        <meta property="og:description" content="Fichaje digital para pymes. Cumple el registro horario obligatorio desde 1,25 €/empleado/mes. 14 días gratis." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://presenzia.app/landing" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:site_name" content="PresenzIA" />
        <meta property="og:image" content="https://presenzia.app/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="PresenzIA — Registro horario digital. Obligatorio por ley, resuelto hoy." />
        {/* Twitter / X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Control Horario para Pymes desde 1,25 € | PresenzIA" />
        <meta name="twitter:description" content="Fichaje digital para pymes. Cumple el registro horario obligatorio desde 1,25 €/empleado/mes. 14 días gratis." />
        <meta name="twitter:image" content="https://presenzia.app/og-image.png" />
        <meta name="twitter:image:alt" content="PresenzIA — Registro horario digital para pymes" />
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "PresenzIA",
          "url": "https://presenzia.app",
          "logo": "https://presenzia.app/logo-presenzia-icon.svg",
          "email": "info@presenzia.app",
          "areaServed": { "@type": "Country", "name": "España" },
          "contactPoint": [{
            "@type": "ContactPoint",
            "email": "info@presenzia.app",
            "contactType": "customer support",
            "availableLanguage": ["es"]
          }, {
            "@type": "ContactPoint",
            "email": "privacidad@presenzia.app",
            "contactType": "Data Protection Officer",
            "availableLanguage": ["es"]
          }]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "PresenzIA",
          "operatingSystem": "Web, iOS, Android",
          "applicationCategory": "BusinessApplication",
          "applicationSubCategory": "Time Tracking",
          "description": "Software de control horario y fichaje digital para pymes y asesorías en España. Cumple el RD-Ley 8/2019.",
          "url": "https://presenzia.app",
          "inLanguage": "es",
          "offers": [
            {
              "@type": "Offer",
              "name": "Plan Pro",
              "price": "1.50",
              "priceCurrency": "EUR",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "1.50",
                "priceCurrency": "EUR",
                "unitText": "empleado/mes"
              }
            },
            {
              "@type": "Offer",
              "name": "Plan Avanzado",
              "price": "2.95",
              "priceCurrency": "EUR",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "2.95",
                "priceCurrency": "EUR",
                "unitText": "empleado/mes"
              }
            }
          ],
          "publisher": { "@type": "Organization", "name": "PresenzIA", "url": "https://presenzia.app" }
        })}</script>
      </Helmet>

      <div className="landing-v2 min-h-screen">
        <Nav audience={audience} onAudienceToggle={toggleAudience} />
        <main>
          <Hero audience={audience} />
          <SocialProof />
          <HowItWorks />
          <UrgencyTimeline />
          <RiskCalculator />
          <LegalBlock />
          <FeaturesSummary />
          <FeaturesDetailed />
          <Kiosco />
          <FichajeAsistido />
          <ProductTour />
          <Sectors />
          <Testimonials />
          <ServiciosContacto />
          <Asesorias />
          <Pricing />
          <FAQ />
          <BlogTeaser />
          <FinalCTA />
        </main>
        <Footer />
      </div>
    </>
  )
}
